import { useTaskPlannedOccurrenceForDate } from '@griply/hooks';
import { getTaskTimeslot, getTimeFormat } from '@griply/utils';
import { useUpdateTimeslot } from 'features/tasks';
import React, { forwardRef } from 'react';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { TimeRangeContextMenu } from 'shared/components/ui/time-range-context-menu';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { Task } from 'shared/types/task';
import { Timeslot } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export type TimeslotContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside' | 'interactionType'
> & {
  task: Task;
  onClose: () => void;
  representedDay: Timestamp;
};

export const TimeslotContextMenu = forwardRef<
  HTMLDivElement,
  TimeslotContextMenuProps
>(({ task, onClose, representedDay, ...rest }, ref) => {
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const timeFormat = getTimeFormat(user);

  const updateTimeslot = useUpdateTimeslot(representedDay);

  const plannedOccurrence = useTaskPlannedOccurrenceForDate(
    task,
    representedDay,
    { weekStartsOn, today },
  );

  const timeslot = getTaskTimeslot(task, { today, plannedOccurrence });

  const onChangeTimeslot = (timeslot: Timeslot | null) =>
    updateTimeslot(task, timeslot);

  return (
    <TimeRangeContextMenu
      ref={ref}
      value={timeslot}
      onChange={onChangeTimeslot}
      onClose={onClose}
      timeFormat={timeFormat}
      {...rest}
    />
  );
});
